import React, { useCallback, useEffect, useMemo, useState } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { useNavigate } from 'react-router-dom';
import {
  Layers, Upload, Play, Database, Download, RotateCcw, AlertTriangle,
  CheckCircle2, Thermometer, GitMerge, Sigma, Info, ArrowRight, Loader2,
} from 'lucide-react';
import { API } from '@/App';

const SOURCES = [
  { key: 'leaf',        label: 'Leaf & Canopy',   hint: 'area_cm2, length, greenness…' },
  { key: 'seed',        label: 'Seed & Fruit',    hint: 'count, mean_length, width…' },
  { key: 'disease',     label: 'Disease & Lesion', hint: 'lesion_pct, lesion_count' },
  { key: 'panicle',     label: 'Panicle / Spike', hint: 'length_cm, spikelet_count' },
  { key: 'root',        label: 'Root Analysis',   hint: 'total_length, diameter, tips' },
  { key: 'nir',         label: 'NIR Prediction',  hint: 'moisture, oil, protein, starch' },
  { key: 'agro_morph',  label: 'Agro-morph CSV',  hint: 'plant_height, yield, DTF' },
  { key: 'environment', label: 'Environment CSV', hint: 'Date, Temp, Humidity, Rainfall (VPD/GDD auto)' },
  { key: 'soil',        label: 'Soil CSV',        hint: 'Optional — pH, OC, NPK…' },
];

const HANDOFFS = [
  { key: 'path_analysis',   label: 'Path Analysis',     target: 'path' },
  { key: 'pca',             label: 'PCA',               target: 'pca-bio' },
  { key: 'd_squared',       label: 'D² Distance',       target: 'd2-cluster' },
  { key: 'heritability',    label: 'Heritability',      target: 'heritability' },
  { key: 'selection_index', label: 'Selection Index',   target: 'selection-index' },
  { key: 'ammi',            label: 'AMMI',              target: 'ammi' },
  { key: 'gge_biplot',      label: 'GGE Biplot',        target: 'gge' },
];

const DEFAULT_NORM = {
  trim: true, uppercase: true, strip_hyphens: true,
  strip_underscores: true, strip_spaces: true,
  regex: '', replacement: '',
};

export default function Integrated() {
  const navigate = useNavigate();
  const [session, setSession] = useState(null);
  const [recon, setRecon] = useState(null);
  const [matrix, setMatrix] = useState(null);
  const [join, setJoin] = useState('inner');
  const [requiredSources, setRequiredSources] = useState([]);
  const [replicate, setReplicate] = useState('mean');
  const [norm, setNorm] = useState(DEFAULT_NORM);
  const [baseTemp, setBaseTemp] = useState(10.0);
  const [heatThresh, setHeatThresh] = useState(35.0);
  const [loading, setLoading] = useState({});
  const cfg = { withCredentials: true };

  const loadSession = useCallback(async () => {
    try {
      const r = await axios.post(`${API}/phenoscan/integrated/session`, {}, cfg);
      setSession(r.data.session);
    } catch (e) {
      // 401/403 = not logged in; surface to devtools but don't toast
      // since this is a background hydration call. Other status codes
      // (network/500) DO get logged for debugging.
      if (e?.response?.status !== 401 && e?.response?.status !== 403) {
        // eslint-disable-next-line no-console
        console.warn('phenoscan loadSession failed:', e?.message || e);
      }
    }
  }, []);
  useEffect(() => { loadSession(); }, [loadSession]);

  const setBusy = (key, v) => setLoading(prev => ({ ...prev, [key]: v }));

  // ─────────────────── Sample bundle loader ───────────────────
  const loadFullBundle = async () => {
    setBusy('bundle', true);
    try {
      const r = await axios.get(`${API}/phenoscan/integrated/validation-sample`, cfg);
      const ds = r.data;
      const sources = ['leaf', 'seed', 'disease', 'panicle', 'nir', 'agro_morph', 'environment'];
      for (const s of sources) {
        const idCol = s === 'environment' ? 'Date' : 'sample_id';
        await axios.post(`${API}/phenoscan/integrated/ingest`,
          { source: s, rows: ds[s], id_col: idCol }, cfg);
      }
      toast.success('Loaded 30-genotype validation bundle — 7 sources ingested');
      await loadSession();
    } catch (e) {
      toast.error(`Bundle load failed: ${e?.response?.data?.detail || e.message}`);
    } finally { setBusy('bundle', false); }
  };

  const loadSourceSample = async (src) => {
    setBusy(`sample-${src}`, true);
    try {
      const r = await axios.get(`${API}/phenoscan/integrated/validation-sample`, cfg);
      const rows = r.data[src];
      if (!rows || rows.length === 0) {
        toast.error(`No validation sample for ${src}`);
        return;
      }
      const idCol = src === 'environment' ? 'Date' : 'sample_id';
      await axios.post(`${API}/phenoscan/integrated/ingest`,
        { source: src, rows, id_col: idCol }, cfg);
      toast.success(`Loaded ${rows.length} rows into ${src}`);
      await loadSession();
    } catch (e) {
      toast.error(`Sample load failed: ${e?.response?.data?.detail || e.message}`);
    } finally { setBusy(`sample-${src}`, false); }
  };

  // ─────────────────── CSV upload per source ───────────────────
  const uploadCsv = async (src, file) => {
    if (!file) return;
    setBusy(`upload-${src}`, true);
    try {
      const fd = new FormData();
      fd.append('source', src);
      fd.append('id_col', src === 'environment' ? 'Date' : 'sample_id');
      fd.append('file', file);
      const r = await axios.post(`${API}/phenoscan/integrated/upload-csv`, fd,
        { ...cfg, headers: { 'Content-Type': 'multipart/form-data' } });
      toast.success(`Uploaded ${r.data.n_rows} rows to ${src}`);
      await loadSession();
    } catch (e) {
      toast.error(`Upload failed: ${e?.response?.data?.detail || e.message}`);
    } finally { setBusy(`upload-${src}`, false); }
  };

  const resetSession = async () => {
    if (!window.confirm('Archive current session and start fresh?')) return;
    setBusy('reset', true);
    try {
      await axios.post(`${API}/phenoscan/integrated/session/reset`, {}, cfg);
      toast.success('Session reset');
      setRecon(null); setMatrix(null);
      await loadSession();
    } finally { setBusy('reset', false); }
  };

  // ─────────────────── Reconcile ───────────────────
  const runReconcile = async () => {
    setBusy('reconcile', true);
    try {
      const r = await axios.post(`${API}/phenoscan/integrated/reconcile`,
        { normalization: { ...norm, regex: norm.regex || null } }, cfg);
      setRecon(r.data);
    } catch (e) {
      toast.error(`Reconcile failed: ${e?.response?.data?.detail || e.message}`);
    } finally { setBusy('reconcile', false); }
  };

  // ─────────────────── Build ───────────────────
  const runBuild = async () => {
    setBusy('build', true);
    try {
      const payload = {
        join,
        replicate_method: replicate,
        normalization: { ...norm, regex: norm.regex || null },
        base_temperature_c: baseTemp,
        heat_threshold_c: heatThresh,
      };
      if (join === 'custom') payload.required_sources = requiredSources;
      const r = await axios.post(`${API}/phenoscan/integrated/build`, payload, cfg);
      setMatrix(r.data);
      toast.success(`Integrated matrix built — ${r.data.n_samples} samples × ${r.data.n_traits} traits`);
    } catch (e) {
      toast.error(`Build failed: ${e?.response?.data?.detail || e.message}`);
    } finally { setBusy('build', false); }
  };

  // ─────────────────── Export ───────────────────
  const exportMatrix = async (fmt) => {
    try {
      const r = await axios.get(`${API}/phenoscan/integrated/export?fmt=${fmt}`,
        { ...cfg, responseType: 'blob' });
      const url = URL.createObjectURL(r.data);
      const a = document.createElement('a');
      a.href = url;
      a.download = `phenoscan_integrated.${fmt}`;
      a.click();
      URL.revokeObjectURL(url);
    } catch (e) {
      toast.error(`Export failed: ${e?.response?.data?.detail || e.message}`);
    }
  };

  // ─────────────────── Hand-off ───────────────────
  // Router-state-only (matches Phase A pattern for existing 6 PhenoScan hand-offs;
  // no localStorage/sessionStorage per Phase A guardrail).
  const sendToAnalysis = (h) => {
    if (!matrix) {
      toast.error('Build the integrated matrix first.');
      return;
    }
    const rowsWithId = matrix.rows.map(r => {
      const { sample_id, ...rest } = r;
      return { sample_id, ...rest };
    });
    navigate('/biometrical-analysis', {
      state: {
        phenoscanHandoff: {
          source: 'phenoscan_integrated',
          target: h.key,
          traits: rowsWithId,
          n_samples: matrix.n_samples,
          n_traits: matrix.n_traits,
          columns: matrix.columns,
        }
      }
    });
  };

  const ingestedSources = useMemo(() =>
    session?.sources ? Object.keys(session.sources) : [],
    [session]);

  return (
    <div data-testid="phenoscan-integrated-pane" className="space-y-6">
      {/* ══════════ Section 1 — Data Sources ══════════ */}
      <Section icon={Database} title="1. Data Sources"
        right={
          <div className="flex gap-2">
            <button onClick={loadFullBundle} disabled={loading.bundle}
              data-testid="load-full-bundle-btn"
              className="px-3 py-1.5 rounded-md text-xs font-semibold flex items-center gap-1.5"
              style={{ background: '#2D6A4F', color: 'white' }}>
              {loading.bundle ? <Loader2 size={12} className="animate-spin" /> : <Play size={12} />}
              Load full validation bundle (30 genotypes)
            </button>
            <button onClick={resetSession} disabled={loading.reset}
              data-testid="reset-session-btn"
              className="px-3 py-1.5 rounded-md text-xs flex items-center gap-1.5"
              style={{ background: 'white', border: '1px solid #E5E5E0', color: '#6F695F' }}>
              <RotateCcw size={12} /> Reset session
            </button>
          </div>
        }>
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-3">
          {SOURCES.map(s => {
            const ingested = session?.sources?.[s.key];
            return (
              <div key={s.key} className="rounded-lg p-3"
                style={{ border: ingested ? '1px solid #2D6A4F' : '1px solid #E5E5E0',
                  background: ingested ? '#F3F6F3' : 'white' }}
                data-testid={`source-card-${s.key}`}>
                <div className="flex items-center justify-between mb-1">
                  <div className="font-semibold text-sm" style={{ color: '#1B4332' }}>{s.label}</div>
                  {ingested && <CheckCircle2 size={14} style={{ color: '#2D6A4F' }} />}
                </div>
                <div className="text-[11px] mb-2" style={{ color: '#6F695F' }}>{s.hint}</div>
                {ingested ? (
                  <div className="text-[11px]" style={{ color: '#2D6A4F' }}>
                    {ingested.rows.length} rows · id: <code>{ingested.id_col}</code>
                  </div>
                ) : null}
                <div className="flex gap-1 mt-2">
                  <button onClick={() => loadSourceSample(s.key)}
                    disabled={loading[`sample-${s.key}`]}
                    data-testid={`load-sample-${s.key}`}
                    className="text-[11px] px-2 py-1 rounded flex items-center gap-1"
                    style={{ background: '#F9FAF9', border: '1px solid #E5E5E0', color: '#2D6A4F' }}>
                    <Play size={10} /> Load sample
                  </button>
                  <label className="text-[11px] px-2 py-1 rounded flex items-center gap-1 cursor-pointer"
                    style={{ background: '#F9FAF9', border: '1px solid #E5E5E0', color: '#6F695F' }}
                    data-testid={`upload-csv-${s.key}`}>
                    <Upload size={10} /> Upload CSV
                    <input type="file" accept=".csv,.xlsx,.xls" className="hidden"
                      onChange={e => uploadCsv(s.key, e.target.files?.[0])} />
                  </label>
                </div>
              </div>
            );
          })}
        </div>
      </Section>

      {/* ══════════ Section 2 — Sample-ID Reconciliation ══════════ */}
      <Section icon={GitMerge} title="2. Sample-ID Reconciliation"
        right={
          <button onClick={runReconcile} disabled={loading.reconcile || ingestedSources.length < 2}
            data-testid="run-reconcile-btn"
            className="px-3 py-1.5 rounded-md text-xs font-semibold flex items-center gap-1.5"
            style={{ background: '#1B4332', color: 'white', opacity: ingestedSources.length < 2 ? 0.5 : 1 }}>
            {loading.reconcile ? <Loader2 size={12} className="animate-spin" /> : <Play size={12} />}
            Reconcile sample IDs
          </button>
        }>
        {/* Normalization toggles */}
        <div className="mb-3">
          <div className="text-xs font-semibold mb-2" style={{ color: '#1B4332' }}>
            ID normalization chain
          </div>
          <div className="flex flex-wrap gap-3 text-xs">
            {[
              ['trim', 'Trim whitespace'],
              ['uppercase', 'Uppercase'],
              ['strip_hyphens', 'Strip hyphens'],
              ['strip_underscores', 'Strip underscores'],
              ['strip_spaces', 'Strip spaces'],
            ].map(([k, lbl]) => (
              <label key={k} className="flex items-center gap-1.5 cursor-pointer" data-testid={`norm-${k}`}>
                <input type="checkbox" checked={norm[k]}
                  onChange={e => setNorm(v => ({ ...v, [k]: e.target.checked }))} />
                <span>{lbl}</span>
              </label>
            ))}
            <div className="flex items-center gap-1.5">
              <span>Regex:</span>
              <input type="text" value={norm.regex}
                onChange={e => setNorm(v => ({ ...v, regex: e.target.value }))}
                placeholder="(optional)"
                data-testid="norm-regex"
                className="px-2 py-0.5 rounded text-xs"
                style={{ border: '1px solid #E5E5E0', width: 140 }} />
            </div>
          </div>
        </div>

        {!recon ? (
          <div className="text-xs" style={{ color: '#6F695F' }}>
            Ingest ≥2 sources, then click <b>Reconcile</b>. The normalisation report and missing-data matrix will appear here.
          </div>
        ) : (
          <>
            {/* Suspicious-intersection warning */}
            {recon.suspicious_intersection && (
              <div className="rounded-lg p-3 mb-3 flex gap-2 items-start"
                style={{ background: '#FEF3C7', border: '1px solid #FDE68A' }}
                data-testid="suspicious-warning">
                <AlertTriangle size={16} style={{ color: '#92400E' }} />
                <div className="text-xs" style={{ color: '#78350F' }}>
                  <b>Suspicious intersection — check ID formats.</b>
                  <ul className="mt-1 list-disc ml-4">
                    {recon.suspicious_reasons.slice(0, 3).map((m, i) => <li key={i}>{m}</li>)}
                  </ul>
                </div>
              </div>
            )}

            {/* Normalization report: before/after table */}
            <div className="mb-3">
              <div className="text-xs font-semibold mb-1" style={{ color: '#1B4332' }}>
                Before / after normalization (up to 3 per source)
              </div>
              <div className="overflow-x-auto">
                <table className="text-[11px] w-full" data-testid="normalization-report">
                  <thead><tr style={{ background: '#F3F6F3' }}>
                    <th className="text-left px-2 py-1">Source</th>
                    <th className="text-left px-2 py-1">Raw ID</th>
                    <th className="text-left px-2 py-1">Normalized</th>
                  </tr></thead>
                  <tbody>
                    {recon.normalization_report.flatMap(sec =>
                      sec.examples.map((ex, i) => (
                        <tr key={`${sec.source}-${i}`} style={{ borderBottom: '1px solid #F3F6F3' }}>
                          <td className="px-2 py-1 font-mono">{sec.source}</td>
                          <td className="px-2 py-1 font-mono">{ex.raw}</td>
                          <td className="px-2 py-1 font-mono" style={{ color: '#2D6A4F' }}>{ex.normalized}</td>
                        </tr>
                      )))}
                  </tbody>
                </table>
              </div>
            </div>

            {/* Counts */}
            <div className="grid grid-cols-2 sm:grid-cols-4 gap-2 mb-3">
              <Stat label="Sources" value={recon.sources.length} />
              <Stat label="Union (N)" value={recon.union_n} testId="union-n" />
              <Stat label="Intersection (N)" value={recon.intersection_n} testId="intersection-n" accent />
              <Stat label="Dropped" value={recon.union_n - recon.intersection_n} />
            </div>

            {/* Missing-data matrix */}
            <div className="mb-3">
              <div className="text-xs font-semibold mb-1" style={{ color: '#1B4332' }}>
                Missing-data matrix · ● present · ○ missing
              </div>
              <div className="overflow-x-auto rounded border" style={{ borderColor: '#E5E5E0' }}>
                <table className="text-[10px]" data-testid="missing-matrix">
                  <thead><tr style={{ background: '#F3F6F3' }}>
                    <th className="px-2 py-1 text-left sticky left-0 bg-[#F3F6F3]">sample_id</th>
                    {recon.sources.map(s => <th key={s} className="px-2 py-1">{s}</th>)}
                  </tr></thead>
                  <tbody>
                    {recon.missing_matrix.slice(0, 40).map(row => (
                      <tr key={row.sample_id} style={{ borderTop: '1px solid #F3F6F3' }}>
                        <td className="px-2 py-0.5 font-mono sticky left-0 bg-white">{row.sample_id}</td>
                        {recon.sources.map(s => (
                          <td key={s} className="px-2 py-0.5 text-center"
                            style={{ color: row[s] ? '#2D6A4F' : '#C026D3', fontSize: 12 }}>
                            {row[s] ? '●' : '○'}
                          </td>
                        ))}
                      </tr>
                    ))}
                  </tbody>
                </table>
                {recon.missing_matrix.length > 40 && (
                  <div className="text-[10px] px-2 py-1" style={{ color: '#6F695F' }}>
                    Showing first 40 of {recon.missing_matrix.length} samples
                  </div>
                )}
              </div>
            </div>

            {/* Join selector */}
            <div className="mt-3">
              <div className="text-xs font-semibold mb-1" style={{ color: '#1B4332' }}>Join strategy</div>
              <div className="flex gap-4 text-xs" data-testid="join-radio">
                {[
                  ['inner', `Intersection (inner) — ${recon.intersection_n} samples`],
                  ['outer', `Union (outer) — ${recon.union_n} samples (NaN where missing)`],
                  ['custom', 'Custom — pick required sources'],
                ].map(([v, lbl]) => (
                  <label key={v} className="flex items-center gap-1.5 cursor-pointer">
                    <input type="radio" checked={join === v} onChange={() => setJoin(v)} />
                    <span>{lbl}</span>
                  </label>
                ))}
              </div>
              {join === 'custom' && (
                <div className="mt-2 flex flex-wrap gap-2 text-[11px]">
                  {recon.sources.map(s => (
                    <label key={s} className="flex items-center gap-1 px-2 py-0.5 rounded cursor-pointer"
                      style={{ background: requiredSources.includes(s) ? '#D1FAE5' : '#F3F6F3' }}>
                      <input type="checkbox" checked={requiredSources.includes(s)}
                        onChange={e => setRequiredSources(prev =>
                          e.target.checked ? [...prev, s] : prev.filter(x => x !== s))} />
                      <span>{s}</span>
                    </label>
                  ))}
                </div>
              )}
            </div>
          </>
        )}
      </Section>

      {/* ══════════ Section 3 — Replicate handling ══════════ */}
      <Section icon={Sigma} title="3. Replicate Handling">
        <div className="flex gap-4 text-xs" data-testid="replicate-radio">
          {[
            ['keep',   'Keep as separate rows'],
            ['mean',   'Collapse to Mean'],
            ['median', 'Collapse to Median'],
          ].map(([v, lbl]) => (
            <label key={v} className="flex items-center gap-1.5 cursor-pointer">
              <input type="radio" checked={replicate === v} onChange={() => setReplicate(v)} />
              <span>{lbl}</span>
            </label>
          ))}
        </div>
        <div className="text-[11px] mt-2" style={{ color: '#6F695F' }}>
          Numeric columns are aggregated; non-numeric columns take the first observed value.
        </div>
      </Section>

      {/* ══════════ Section 4 — Environment Integration ══════════ */}
      <Section icon={Thermometer} title="4. Environment Integration">
        <div className="flex flex-wrap items-center gap-4 text-xs">
          <label className="flex items-center gap-2" data-testid="env-base-temp">
            <span>Base T (°C):</span>
            <input type="number" step="0.1" value={baseTemp}
              onChange={e => setBaseTemp(parseFloat(e.target.value) || 10)}
              className="px-2 py-0.5 rounded w-16"
              style={{ border: '1px solid #E5E5E0' }} />
          </label>
          <label className="flex items-center gap-2" data-testid="env-heat-thresh">
            <span>Heat stress ≥ (°C):</span>
            <input type="number" step="0.1" value={heatThresh}
              onChange={e => setHeatThresh(parseFloat(e.target.value) || 35)}
              className="px-2 py-0.5 rounded w-16"
              style={{ border: '1px solid #E5E5E0' }} />
          </label>
          <div style={{ color: '#6F695F' }}>
            VPD (Tetens) and GDD auto-computed per day. Required columns: Date, Temperature_C, Humidity_pct, Rainfall_mm.
          </div>
        </div>
        {matrix?.environment_summary && (
          <div className="mt-3 grid grid-cols-2 sm:grid-cols-4 gap-2" data-testid="env-summary">
            <Stat label="Days" value={matrix.environment_summary.n_days} />
            <Stat label="Total GDD" value={matrix.environment_summary.total_gdd} />
            <Stat label="Mean VPD (kPa)" value={matrix.environment_summary.mean_vpd_kpa} />
            <Stat label="Heat days" value={matrix.environment_summary.heat_stress_days} />
          </div>
        )}
      </Section>

      {/* ══════════ Section 5 — Integrated Trait Matrix ══════════ */}
      <Section icon={Layers} title="5. Integrated Trait Matrix"
        right={
          <div className="flex gap-2">
            <button onClick={runBuild} disabled={loading.build || ingestedSources.length < 2}
              data-testid="build-matrix-btn"
              className="px-3 py-1.5 rounded-md text-xs font-semibold flex items-center gap-1.5"
              style={{ background: '#2D6A4F', color: 'white', opacity: ingestedSources.length < 2 ? 0.5 : 1 }}>
              {loading.build ? <Loader2 size={12} className="animate-spin" /> : <Play size={12} />}
              Build integrated matrix
            </button>
            {matrix && (
              <>
                <button onClick={() => exportMatrix('csv')}
                  data-testid="export-matrix-csv"
                  className="px-3 py-1.5 rounded-md text-xs flex items-center gap-1.5"
                  style={{ background: 'white', border: '1px solid #2D6A4F', color: '#2D6A4F' }}>
                  <Download size={12} /> CSV
                </button>
                <button onClick={() => exportMatrix('xlsx')}
                  data-testid="export-matrix-xlsx"
                  className="px-3 py-1.5 rounded-md text-xs flex items-center gap-1.5"
                  style={{ background: 'white', border: '1px solid #2D6A4F', color: '#2D6A4F' }}>
                  <Download size={12} /> XLSX
                </button>
              </>
            )}
          </div>
        }>
        {!matrix ? (
          <div className="text-xs" style={{ color: '#6F695F' }}>
            Pick a join strategy then click <b>Build integrated matrix</b>. The trait table and data dictionary appear here.
          </div>
        ) : (
          <>
            <div className="grid grid-cols-2 sm:grid-cols-4 gap-2 mb-3">
              <Stat label="Samples" value={matrix.n_samples} accent testId="matrix-n-samples" />
              <Stat label="Traits" value={matrix.n_traits} testId="matrix-n-traits" />
              <Stat label="Sources merged" value={matrix.data_dictionary
                ? new Set(matrix.data_dictionary.map(d => d.source)).size : 0} />
              <Stat label="Excluded" value={matrix.excluded?.length || 0} />
            </div>

            {/* Matrix preview table */}
            <div className="overflow-auto rounded border" style={{ borderColor: '#E5E5E0', maxHeight: 320 }}>
              <table className="text-[11px]" data-testid="matrix-table">
                <thead><tr style={{ background: '#F3F6F3', position: 'sticky', top: 0 }}>
                  {matrix.columns.map(c => (
                    <th key={c} className="px-2 py-1 text-left whitespace-nowrap" style={{ color: '#1B4332' }}>
                      {c}
                    </th>
                  ))}
                </tr></thead>
                <tbody>
                  {matrix.rows.slice(0, 50).map((row, i) => (
                    <tr key={i} style={{ borderTop: '1px solid #F3F6F3' }}>
                      {matrix.columns.map(c => (
                        <td key={c} className="px-2 py-0.5 whitespace-nowrap">
                          {row[c] === null || row[c] === undefined
                            ? <span style={{ color: '#C026D3' }}>—</span>
                            : typeof row[c] === 'number' ? row[c].toFixed(3) : String(row[c])}
                        </td>
                      ))}
                    </tr>
                  ))}
                </tbody>
              </table>
              {matrix.rows.length > 50 && (
                <div className="text-[10px] px-2 py-1" style={{ color: '#6F695F' }}>
                  Showing first 50 of {matrix.rows.length} rows.
                </div>
              )}
            </div>

            {/* Summary stats */}
            <details className="mt-3">
              <summary className="text-xs cursor-pointer" style={{ color: '#2D6A4F' }}>
                Per-trait summary statistics
              </summary>
              <div className="overflow-x-auto mt-2">
                <table className="text-[11px]" data-testid="trait-summary">
                  <thead><tr style={{ background: '#F3F6F3' }}>
                    {['Column', 'N', 'Missing %', 'Mean', 'SD', 'CV %', 'Min', 'Max'].map(h =>
                      <th key={h} className="px-2 py-1 text-left">{h}</th>)}
                  </tr></thead>
                  <tbody>
                    {matrix.summary.per_column.map(row => (
                      <tr key={row.column} style={{ borderTop: '1px solid #F3F6F3' }}>
                        <td className="px-2 py-0.5 font-mono">{row.column}</td>
                        <td className="px-2 py-0.5">{row.n}</td>
                        <td className="px-2 py-0.5">{row.missing_pct}</td>
                        <td className="px-2 py-0.5">{row.mean ?? '—'}</td>
                        <td className="px-2 py-0.5">{row.sd ?? '—'}</td>
                        <td className="px-2 py-0.5">{row.cv_pct ?? '—'}</td>
                        <td className="px-2 py-0.5">{row.min ?? '—'}</td>
                        <td className="px-2 py-0.5">{row.max ?? '—'}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </details>

            {/* Data dictionary */}
            <details className="mt-2">
              <summary className="text-xs cursor-pointer" style={{ color: '#2D6A4F' }}>
                Data dictionary
              </summary>
              <div className="overflow-x-auto mt-2">
                <table className="text-[11px]" data-testid="data-dictionary">
                  <thead><tr style={{ background: '#F3F6F3' }}>
                    {['Column', 'Source', 'Display name', 'Unit', 'Method'].map(h =>
                      <th key={h} className="px-2 py-1 text-left">{h}</th>)}
                  </tr></thead>
                  <tbody>
                    {matrix.data_dictionary.map(d => (
                      <tr key={d.column} style={{ borderTop: '1px solid #F3F6F3' }}>
                        <td className="px-2 py-0.5 font-mono">{d.column}</td>
                        <td className="px-2 py-0.5">{d.source}</td>
                        <td className="px-2 py-0.5">{d.display_name}</td>
                        <td className="px-2 py-0.5">{d.unit}</td>
                        <td className="px-2 py-0.5">{d.method}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </details>

            {/* Excluded samples */}
            {matrix.excluded?.length > 0 && (
              <details className="mt-2">
                <summary className="text-xs cursor-pointer" style={{ color: '#92400E' }}>
                  {matrix.excluded.length} samples excluded (click to view)
                </summary>
                <div className="text-[10px] mt-2 p-2 rounded"
                  style={{ background: '#FFFBEB', color: '#78350F', maxHeight: 200, overflow: 'auto' }}
                  data-testid="excluded-list">
                  {matrix.excluded.map((e, i) => (
                    <div key={i}><code>{e.sample_id}</code> missing in: {e.missing_in.join(', ')}</div>
                  ))}
                </div>
              </details>
            )}
          </>
        )}
      </Section>

      {/* ══════════ Section 6 — Statistical Hand-off ══════════ */}
      <Section icon={ArrowRight} title="6. Statistical Hand-off">
        <div className="text-xs mb-3" style={{ color: '#6F695F' }}>
          Each button sends the integrated matrix to the target BKAgriD analysis page with the trait matrix pre-filled.
          The target page shows a module-specific warning if data coverage is below recommended.
        </div>
        <div className="flex flex-wrap gap-2">
          {HANDOFFS.map(h => (
            <button key={h.key} onClick={() => sendToAnalysis(h)}
              disabled={!matrix}
              data-testid={`handoff-${h.key}`}
              className="px-3 py-2 rounded-md text-xs font-semibold flex items-center gap-1.5"
              style={{
                background: matrix ? '#2D6A4F' : '#E5E5E0',
                color: matrix ? 'white' : '#A0988A',
                cursor: matrix ? 'pointer' : 'not-allowed',
              }}>
              <ArrowRight size={12} /> {h.label}
            </button>
          ))}
        </div>
        {!matrix && (
          <div className="text-[11px] mt-2 flex items-center gap-1.5" style={{ color: '#92400E' }}>
            <Info size={11} /> Build the integrated matrix first to enable hand-off.
          </div>
        )}
      </Section>
    </div>
  );
}

// ─────────────── Small subcomponents ───────────────
function Section({ icon: Icon, title, right, children }) {
  return (
    <div className="rounded-xl bg-white p-4" style={{ border: '1px solid #E5E5E0' }}>
      <div className="flex items-center justify-between mb-3">
        <div className="flex items-center gap-2">
          <Icon size={16} style={{ color: '#2D6A4F' }} />
          <div className="font-display text-base" style={{ color: '#1B4332' }}>{title}</div>
        </div>
        {right}
      </div>
      {children}
    </div>
  );
}

function Stat({ label, value, accent, testId }) {
  return (
    <div className="rounded-md p-2" style={{ background: accent ? '#D1FAE5' : '#F3F6F3' }}
      data-testid={testId}>
      <div className="text-[10px] uppercase" style={{ color: '#6F695F', letterSpacing: 0.5 }}>{label}</div>
      <div className="font-semibold" style={{ color: accent ? '#065F46' : '#1B4332', fontSize: 16 }}>
        {value}
      </div>
    </div>
  );
}
